<template>
    <v-form @submit.prevent="addUpdate" ref="form">
        <v-card style="border-radius:4px;">
            <v-toolbar flat height="45px">
                <span class="font-weight-bold ">{{mode}} Other Benefits</span>
                <v-spacer></v-spacer>
                <help-link appId="compensation" pageId="add-other-benefits" color="black"></help-link>
                <v-btn icon @click.native="closeDialog()">
                    <v-icon >close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card-text>
                <v-alert v-if="error" :value="true" color="error" icon="error" outline>
                    Error encountered. Please contact support. Error details: {{errorMessage}}
                </v-alert>
                <v-layout row wrap> 
                    <v-flex xs12 sm3 class="form-row">       
                        <v-select
                            label="Year"
                            :items="isEditMode?fiscal_years_all:fiscal_years"
                            :rules="[rules.required]" 
                            item-text="fiscalYear"
                            item-value="fiscalPeriodId"
                            :disabled="disabled||isEditMode"
                            v-model="fiscalPeriodId"
                        ></v-select> 
                    </v-flex>
                    <v-flex xs12 sm3 class="form-row">                
                        <v-select
                            label="Type"
                            :items="benefit_types"
                            :rules="[rules.required]" 
                            item-text="name"
                            item-value="benefitTypeId"
                            :disabled="disabled||isEditMode"
                            v-model="benefitTypeId"
                            @change="checkBenefitType"
                        ></v-select>
                    </v-flex>
                    <v-flex v-if="benefitTypeId" xs12 sm6 class="form-row">  
                        <v-text-field label="Other Benefit Description" 
                            v-model="otherBenefitDescription" 
                            v-if="!is13thMonthPay"
                            :rules="[rules.required]"
                            ></v-text-field>  
                        <v-select
                            v-if="is13thMonthPay"
                            label="13th Month Rate"
                            :items="thirteenth_month_rate_types"
                            :rules="[rules.required]" 
                            item-text="description"
                            item-value="type"
                            :disabled="disabled"
                            :hint="thirteenthMonthHint"
                            persistent-hint
                            v-model="thirteenthMonthPayRate"        
                        ></v-select>      
                    </v-flex>
                </v-layout>
                <v-layout row wrap v-if="benefitTypeId&&!is13thMonthPay">
                    <v-flex xs12 sm6 class="form-row">                
                        <v-text-field label="Monthly Amount" 
                            v-model="monthlyAmount" 
                            :disabled="disabled"
                            :rules="[rules.required_number,rules.max_amount]"
                            type="number" min="1" step="1" value="0.00" 
                            @blur="computeAnnualPayment"
                            ></v-text-field>  
                    </v-flex>
                    <v-flex xs12 sm6 class="form-row">    
                         <v-text-field label="Annual Payment" 
                            v-model="annualAmount" 
                            :disabled="disabled"
                            readonly
                            hint="Readonly, auto computed"
                            ></v-text-field>   
                    </v-flex>
                </v-layout>
                <v-layout row wrap> 
                    <v-flex xs12 sm8 class="form-row"> 
                        <v-select
                            v-model="payOnMonth2"
                            :items="futureMonths"
                            label="Payout On"
                            :rules="[rules.required_multiple]"
                            :disabled="disabled"
                            item-text="month"
                            item-value="value"
                            multiple
                            chips
                            @change="computeAnnualPayment"
                            >
                            <!-- SELECT ALL -->
                            <template v-slot:prepend-item>
                                <v-list-tile ripple @click="toggleAllMonths">
                                <v-list-tile-action>
                                    <v-icon :color="payOnMonth2.length ? 'indigo darken-4' : ''">
                                        {{ selectAllIcon }}
                                    </v-icon>
                                </v-list-tile-action>
                                <v-list-tile-content>
                                    <v-list-tile-title>Select All</v-list-tile-title>
                                </v-list-tile-content>
                                </v-list-tile>
                                <v-divider class="mt-2"></v-divider>
                            </template>
                        </v-select>
                    </v-flex>
                    <v-flex xs12 sm4 class="form-row">                
                        <v-select
                            v-if="cutoffSettings.type!=1"
                            label="Payout Schedule"
                            :items="payoutTypes"
                            :rules="[rules.required]" 
                            item-text="description"
                            item-value="payoutMode"
                            :disabled="disabled"
                            v-model="payoutMode"           
                        ></v-select>      
                    </v-flex>
                </v-layout>
                <v-layout row wrap> 
                    <v-flex xs12 class="form-row">  
                        <v-alert v-if="!is13thMonthPay" :value="true" color="info" icon="info" outline>
                            Other benefits will be taxable as compensation income subject to normal income tax rate.
                        </v-alert>
                    </v-flex>
                </v-layout>
                <v-layout row wrap v-if="showComment"> 
                    <v-flex xs12 sm12 class="form-comment"> 
                        <v-textarea v-model="remarks" label="Comments" rows="2" color="primary"></v-textarea>
                    </v-flex>
                </v-layout>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn icon @click="showComment=!showComment" title="Add Comment">
                    <v-icon>comment</v-icon>
                </v-btn>
                <v-btn v-if="!isEditMode" @click="clearForm" :disabled="disabled">clear</v-btn>
                <v-btn color="primary" type="submit" :disabled="disabled" :loading="loading" dark class="mr-20">{{mode}}</v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>
<script>

import benefitsApi from '@/api/benefits'
import payoutTypes from '@/constants/payout-types'

export default {
    data () {
        return {
            payoutTypes,
            email: null,
            loading: false,
            disabled: false,
            error: false,
            errorMessage: null,
            rules: {
                required: value => !!value || 'Required field',
                required_multiple: value => value.length>0 || 'Must select at least one month.',
                required_number: value => !!value || 'Required field. Number only.',
                max_amount: value => value<=1000000 || 'Invalid Amount. Maximum of 1,000,000.00' 
            },
            months_list: [
                {month: "January", value: 1},
                {month: "February", value: 2},
                {month: "March", value: 3},
                {month: "April", value: 4},
                {month: "May", value: 5},
                {month: "June", value: 6},
                {month: "July", value: 7},
                {month: "August", value: 8},
                {month: "September", value: 9},
                {month: "October", value: 10},
                {month: "November", value: 11},
                {month: "December", value: 12}
            ],
            thirteenth_month_rate_types: [
                {type: 1, description: 'Pro-rated', hint: ''},
                {type: 2, description: 'Current salary', hint: 'Salary as of payroll generation date'}
            ],
            mode: null,
            showComment: false,
            otherBenefitsAllocationId: "0",
            fiscalPeriodId: null,
            selectedUserId: null,
            benefitTypeId: null,
            otherBenefitDescription: null,
            monthlyAmount: null,
            payOnMonth: null,
            payOnMonth2: [],
            payoutMode: null,
            recurringAnnually: false,
            remarks: "",
            months: [
                {month: "January", value: 1},
                {month: "February", value: 2},
                {month: "March", value: 3},
                {month: "April", value: 4},
                {month: "May", value: 5},
                {month: "June", value: 6},
                {month: "July", value: 7},
                {month: "August", value: 8},
                {month: "September", value: 9},
                {month: "October", value: 10},
                {month: "November", value: 11},
                {month: "December", value: 12}
            ],
            benefit_types: [],
            description: null,
            type: 'Other',
            annualThreshold: null,
            annualAmount: null,
            thirteenthMonthPayRate: 1 //default to Pro-rated
        }
    },
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
    }, 
    methods: {
        async setAdd(data) {
            this.error = false
            this.mode = "Add"

            if (!this.benefit_types.length) {
                await this.getBenefitTypes()
            }

            this.selectedUserId = data.userId
            this.clearForm()
        },
        async setUpdate(data) {
            this.error = false
            this.mode = "Update"

            if (!this.benefit_types.length) {
                await this.getBenefitTypes()
            }
            
            this.selectedUserId = data.userId
            this.fiscalPeriodId = data.otherbenefits.fiscalPeriodId
            this.otherBenefitsAllocationId = data.otherbenefits.otherBenefitsAllocationId
            this.benefitTypeId = data.otherbenefits.benefitTypeId
            this.otherBenefitDescription = data.otherbenefits.otherBenefitDescription
            this.monthlyAmount = data.otherbenefits.monthlyAmount
            this.payOnMonth = parseInt(data.otherbenefits.payOnMonth)
            this.payOnMonth2 = data.otherbenefits.payOnMonth2
            this.payoutMode = data.otherbenefits.payoutMode
            this.remarks = ""

            if (!this.is13thMonthPay) {
                this.computeAnnualPayment()
            }
        },
        async addUpdate() {
            try {
                this.loading = true
                this.disabled = true
                if (this.$refs.form.validate()) {
                    var data = {
                        otherBenefitsAllocationId: this.otherBenefitsAllocationId,
                        fiscalPeriodId: this.fiscalPeriodId,
                        userId: this.selectedUserId,
                        benefitTypeId: this.benefitTypeId,      
                        otherBenefitDescription: this.otherBenefitDescription,                
                        monthlyAmount: this.monthlyAmount,
                        payOnMonth: this.payOnMonth,
                        payOnMonth2: this.payOnMonth2.sort((a,b) => a-b),

                        //if 2 cutoffs per month: 1-payout on first cutoff, 2 payout on 2nd cutoff, 3 divide amount to 2 cutoffs
	                    //if 1 cutoff per month: 4-payout all in one cutoff
                        payoutMode: this.isBiMonthlyCutoff ? this.payoutMode : 4,
                        recurringAnnually: this.recurringAnnually,
                        thirteenthMonthPayRate: this.thirteenthMonthPayRate,
                        remarks: this.remarks,

                        //used for update history only, not saved in table
                        otherBenefitName: this.selectedBenefitName,
                        fiscalYear: this.selectedFiscalYear
                    }
                    if (this.mode == 'Add') {
                        const response = await benefitsApi.addOtherBenefits(data)
                        if (response.status == 200) {
                            this.$emit('send', '')
                        }
                    }else{
                        const response = await benefitsApi.updateOtherBenefits(data)
                        if (response.status == 200) {
                            this.$emit('send', '')
                        }
                    }
                    this.closeDialog()
                }
                this.loading = false
                this.disabled = false
            }catch (error) {
                this.error = true
                this.errorMessage = error
            }
        },
        toggleAllMonths() {
            this.$nextTick(() => {
                if (this.allMonthsSelected) {
                    this.payOnMonth2 = []
                } else {
                    this.payOnMonth2 = this.allMonthValues.slice()
                }
                this.computeAnnualPayment()
            })
        },
        computeAnnualPayment() {
            const multiplier = this.payOnMonth2.length ? this.payOnMonth2.length : 1
            this.annualAmount = 0
            if (this.monthlyAmount > 0) {
                this.annualAmount = this.monthlyAmount * multiplier
            }
        },
        checkBenefitType() {
            this.otherBenefitDescription = null
            this.months = JSON.parse(JSON.stringify(this.months_list)) //deep clone
            this.payOnMonth = null
            if (this.is13thMonthPay) {
                this.otherBenefitDescription = 'NA'
            }
        },
        async getBenefitTypes() {
            try {
                const response = await benefitsApi.getBenefitTypes()
                if (response && response.status == 200) {
                    this.benefit_types = response.data.items.filter(item => {
                        return item.type == 'Other'
                    })
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        closeDialog() {
            this.$emit('update:dialogAddEditOtherBenefits', false)
        },
        clearForm() {
            this.$refs.form.reset()
            this.remarks = ''
            this.loading = false
            this.error = false
            this.errorMessage = ''
        },
    },
    computed: {
        is13thMonthPay() {
            const otherBenefitName = this.benefit_types.find(x => x.benefitTypeId == this.benefitTypeId)
            if (otherBenefitName) 
                return otherBenefitName.name == '13th Month'
            return false
        }, 
        thirteenthMonthHint() {
            const data = this.thirteenth_month_rate_types.find(x => x.type == this.thirteenthMonthPayRate)
            if (data) return data.hint
            return ''
        },
        cutoffSettings() {
            return this.$store.state.cutOffSettings
        },
        isEditMode() {
            return this.mode == 'Update'
        },
        isBiMonthlyCutoff() {
            if (this.cutoffSettings) {
                return (this.cutoffSettings.second_From && this.cutoffSettings.second_To)
            }
        },
        futureMonths() {
            const currentMonth = new Date().getMonth() + 1; 
            this.months.forEach(item => {
                //item.disabled = item.value < currentMonth
                item.disabled  = false //always enable all months
            })
            return this.months
        },
        fiscal_years() {
            const currentYear = new Date().getFullYear()
            if (this.$store.state.fiscalPeriods) {
                return this.$store.state.fiscalPeriods.filter(x => x.fiscalYear >= currentYear)
            }
            return [{fiscalYear: currentYear}]
        },
        fiscal_years_all() {
            const currentYear = new Date().getFullYear()
            if (this.$store.state.fiscalPeriods) {
                return this.$store.state.fiscalPeriods
            }
            return [{fiscalYear: currentYear}]
        },
        allMonthValues() {
            return this.futureMonths.map(m => m.value)
        },
        allMonthsSelected() {
            return this.payOnMonth2.length === this.allMonthValues.length
        },
        someMonthsSelected() {
            return this.payOnMonth2.length > 0 && !this.allMonthsSelected
        },
        selectAllIcon() {
            if (this.allMonthsSelected) return 'check_box'
            if (this.someMonthsSelected) return 'indeterminate_check_box'
            return 'check_box_outline_blank'
        },
        selectedBenefitName() {
            if (this.benefitTypeId) 
                return this.benefit_types.find(x => x.benefitTypeId == this.benefitTypeId).name
        },
        selectedFiscalYear() {
            if (this.fiscalPeriodId)
                return this.fiscal_years_all.find(x => x.fiscalPeriodId == this.fiscalPeriodId).fiscalYear
        }
    },
}
</script>
<style scoped>
.form-row {
    padding: 0px 15px 0px 15px;
}
.form-comment {
    margin:5px 15px -25px 15px;
}
.form-switch {
    margin:0px 0px 10px 5px;
}
</style>